import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Spin} from 'antd';
import {api} from '../../../config';
import ContractViewerV2 from './ContractViewerV2';
import {buildCommentCitationNavigation, citationDocumentOptions, citationHighlightBlocks} from '../../../utils/citationNavigation';

const getViewUrl = response => response?.data?.data?.url || response?.data?.url || null;

const normalizeBbox = bbox => {
    if (!bbox) return null;
    if (Array.isArray(bbox) && bbox.length === 4) {
        bbox = {left: bbox[0], top: bbox[1], width: bbox[2], height: bbox[3]};
    }
    const normalized = {
        left: Number(bbox.left ?? bbox.Left),
        top: Number(bbox.top ?? bbox.Top),
        width: Number(bbox.width ?? bbox.Width),
        height: Number(bbox.height ?? bbox.Height),
    };
    return Object.values(normalized).every(Number.isFinite)
        && normalized.left >= 0 && normalized.top >= 0
        && normalized.width > 0 && normalized.height > 0
        && normalized.left + normalized.width <= 1.000001
        && normalized.top + normalized.height <= 1.000001 ? normalized : null;
};

export default function ComparisonVersionEvidenceViewer({source, navigationRequest, comments = null, selectedRef = null, onBlockClick, onBlockHover, isActive = true, hideCitationDocumentSelector = false}) {
    const [viewUrl, setViewUrl] = useState(null);
    const [error, setError] = useState(false);
    const [pageCount, setPageCount] = useState(null);
    const [selectedSourceVersion, setSelectedSourceVersion] = useState(null);
    const documentVersionId = source.isCitation ? selectedSourceVersion || source.documentVersionId : source.documentVersionId;
    const options = citationDocumentOptions(navigationRequest?.comment);
    const evidence = useMemo(() => source.isCitation && navigationRequest?.comment
        ? buildCommentCitationNavigation(navigationRequest.comment, documentVersionId)?.evidence
        : navigationRequest?.evidence, [source.isCitation, navigationRequest?.comment, navigationRequest?.evidence, documentVersionId]);
    const page = Number(evidence?.page);
    const citationDocumentMatches = !source.isCitation || evidence?.document_version_id === documentVersionId;
    const pageAvailable = citationDocumentMatches && Number.isInteger(page) && page > 0
        && evidence?.resolution_status !== 'document_only' && evidence?.resolution_status !== 'unresolved'
        && (pageCount === null || page <= pageCount);
    const evidenceRef = source.isCitation && evidence?.comment_id ? evidence.comment_id : navigationRequest?.requestId
        ? `comparison-version-evidence:${navigationRequest.requestId}`
        : null;
    const highlightBlocks = useMemo(() => {
        if (source.isCitation) {
            if (comments) return citationHighlightBlocks(comments, documentVersionId).filter(part => pageCount === null || part.page <= pageCount);
            if (!evidenceRef || evidence?.document_version_id !== documentVersionId || evidence?.resolution_status !== 'resolved') return [];
            return (evidence.highlights || []).filter(part => (
                part.resolution_status === 'resolved'
                && part.document_version_id === documentVersionId
                && Number.isInteger(part.page) && part.page > 0
                && (pageCount === null || part.page <= pageCount)
                && normalizeBbox(part.bbox)
            )).map(part => ({
                ref: evidenceRef, page: part.page, bbox: normalizeBbox(part.bbox),
                text: part.excerpt || '', kind: 'resolved_source_citation',
                blockId: part.block_id || part.citation_id,
                file_id: part.file_id, document_version_id: part.document_version_id, source_citation_id: part.source_citation_id,
            }));
        }
        const bbox = normalizeBbox(evidence?.bbox);
        if (!evidenceRef || !bbox || !pageAvailable) return [];
        return [{
            ref: evidenceRef,
            page,
            bbox,
            text: evidence.excerpt || '',
            kind: 'comparison_version_evidence',
            blockId: evidence.block_id,
        }];
    }, [comments, documentVersionId, evidence, evidenceRef, page, pageAvailable, pageCount, source.isCitation]);

    useEffect(() => {setSelectedSourceVersion(null);}, [navigationRequest?.requestId, source.documentVersionId]);

    useEffect(() => {
        let active = true;
        setViewUrl(null);
        setError(false);
        setPageCount(null);
        api.get(`documents/versions/${documentVersionId}/view-url`, {params: {disposition: 'inline'}})
            .then(response => {
                if (!active) return;
                const url = getViewUrl(response);
                if (!url) throw new Error('missing_view_url');
                setViewUrl(url);
            })
            .catch(() => active && setError(true));
        return () => { active = false; };
    }, [documentVersionId]);

    if (error) {
        return <Alert type="error" showIcon message="Cette version est inaccessible. Aucune autre version ne sera ouverte." />;
    }
    if (!viewUrl) return <div className="comparison-version-viewer__loading"><Spin /></div>;

    return <>
        {source.isCitation && options.length > 1 && !hideCitationDocumentSelector && <label className="citation-document-selector">Document source{' '}
            <select aria-label="Document source" value={documentVersionId} onChange={event => setSelectedSourceVersion(event.target.value)}>
                {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
        </label>}
        {source.isCitation && evidence?.citation_partial && <Alert type="info" showIcon
            message="Localisation à vérifier pour une partie de cet extrait. Seuls les passages retrouvés sont surlignés." />}
        {!(source.isCitation ? highlightBlocks.some(part => part.ref === evidenceRef) : highlightBlocks.length) && <Alert type="info" showIcon
            message={source.isCitation ? 'Localisation à vérifier pour ce commentaire.' : 'Emplacement précis indisponible. La version demandée reste ouverte sans surlignage.'} />}
        <ContractViewerV2
        fileUrl={viewUrl}
        textractBlocks={highlightBlocks}
        selectedRef={highlightBlocks.length ? (source.isCitation ? selectedRef || evidenceRef : evidenceRef) : null}
        requestedPage={pageAvailable ? page : null}
        requestedCitationId={evidence?.source_citation_id || evidence?.citation_id}
        navigationKey={`${navigationRequest?.requestId || ''}:${documentVersionId}`}
        isActive={isActive}
        onBlockClick={source.isCitation ? onBlockClick : undefined}
        onBlockHover={source.isCitation ? onBlockHover : undefined}
        onPageCount={setPageCount}
        onLoadError={() => setError(true)}
    /></>;
}
